import { EmailRounded, LinkRounded, SmsRounded } from '@mui/icons-material';
import { Stack, StackProps } from '@mui/material';
import { VssPlanType } from '@portal-middleware/hooks';
import { useNotifications } from '@vestwell-frontend/hooks';
import { Text } from '@vestwell-frontend/ui';

import { FC, useCallback, useMemo } from 'react';
import { useCopyToClipboard } from 'react-use';

import { useGiftStateContext } from '../contexts/GiftStateContext';
import CircleIconButton from './CircleIconButton';

type ShareGiftPageProps = {} & StackProps;

export const ShareGiftPage: FC<ShareGiftPageProps> = ({ ...stackProps }) => {
  const [state, copyToClipboard] = useCopyToClipboard();
  const notify = useNotifications();
  const { giftAndAccount, plan } = useGiftStateContext();

  const planName = plan?.data?.data?.shortName || '';
  const firstName = giftAndAccount?.data?.data?.accountPublic?.firstName || '';
  const shareUrl = giftAndAccount?.data?.data?.giftAccount?.shareUrl || '';

  const emailLink = useMemo(() => {
    const subject = `Need a gift idea for ${firstName}?`;
    const body = `Contribute to their ${planName} account.

If you're looking for the perfect gift for ${firstName},\
 check out their gifting page to make a contribution to their ${planName} account.

View ${firstName}'s gifting page:

${shareUrl}?utm_source=gifting&utm_medium=email`;
    return `&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      body
    )}`;
  }, [firstName, planName, shareUrl]);

  const smsCopy = useMemo(() => {
    return `Contribute to ${firstName}'s ${plan?.data?.data?.type === VssPlanType.able ? 'ABLE' : 'college'} savings account\n${shareUrl}?utm_source=gifting&utm_medium=sms`;
  }, [firstName, plan, shareUrl]);

  const onCopy = useCallback(() => {
    copyToClipboard(shareUrl);
    notify.success(`Copied share url to clipboard`);
  }, [copyToClipboard, notify, shareUrl]);

  return (
    <Stack {...stackProps} mb={{ sm: 4, xs: 4 }}>
      <Text
        aria-level={3}
        display={{ md: 'block', sm: 'none', xs: 'none' }}
        mb={2}
        role='heading'
        textAlign={{ md: 'left', sm: 'center', xs: 'center' }}
        variant='g2'>
        Share gifting page
      </Text>
      <Stack
        flexDirection='row'
        gap={2}
        justifyContent={{ md: 'start', sm: 'center', xs: 'center' }}>
        <CircleIconButton
          color='primary'
          disabled={Boolean(state.error)}
          icon={<LinkRounded />}
          label={
            <Text color='grey100' mb={0} textAlign='center' variant='j2'>
              Copy Link
            </Text>
          }
          onClick={onCopy}
        />
        <CircleIconButton
          color='primary'
          href={`sms:?&body=${encodeURIComponent(smsCopy)}`}
          icon={<SmsRounded />}
          label={
            <Text color='grey100' mb={0} textAlign='center' variant='j2'>
              SMS
            </Text>
          }
        />
        <CircleIconButton
          color='primary'
          href={`mailto:?${emailLink}`}
          icon={<EmailRounded />}
          label={
            <Text color='grey100' mb={0} textAlign='center' variant='j2'>
              Email
            </Text>
          }
        />
      </Stack>
    </Stack>
  );
};
